import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type {
  BreedablePokemon,
  BreedingResponse,
  CompatibilityDetail,
  EligibleResponse,
  NurseryPair,
} from '../lib/types';
import { Button, EmptyState, ErrorState, Panel } from '../components/ui';
import { EggArt } from '../components/EggArt';
import { CandidateCard } from '../components/breeding/CandidateCard';
import { formatDuration, genderSymbol } from '../lib/format';

/**
 * The Nursery. Several slots, each holding a pair.
 *
 * A pair's Egg Family is resolved from the breeding rules the moment it is
 * created, so the card can name the family it will produce while keeping the
 * species inside as secret as any market egg. Learning which pairing makes
 * which family is the reward.
 */
export function NurseryPage() {
  const { refresh } = useSession();
  const { push } = useToast();
  const state = useAsync<BreedingResponse>(() => api.get('/breeding'));

  const [picked, setPicked] = useState<BreedablePokemon[]>([]);
  const [compat, setCompat] = useState<CompatibilityDetail | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const now = useNow();

  const first = picked.length === 1 ? picked[0]! : null;

  /* --- The picker ---------------------------------------------------------
     Search, sort and paging are all server-side query parameters. The tray
     used to be handed the first 200 Pokemon by level and nothing else — a wall
     a serious breeder hits immediately, in close to the least useful order for
     choosing a parent. None of this is filtered here: sorting the page you
     already have is not sorting your collection.

     Compatibility comes back on the rows themselves rather than from a second
     request. The egg-group rules, the cooldown and the event-Pokemon guards
     live in `checkCompatibility`, and a copy in the client is a copy that can
     disagree with the one that decides. */
  const [sort, setSort] = useState(() => localStorage.getItem('nursery.sort') ?? 'iv');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => localStorage.setItem('nursery.sort', sort), [sort]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search), 280);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Any change to *what* is listed starts again at page 1: page 7 of a search
  // that now matches four Pokemon is an empty screen.
  useEffect(() => setPage(1), [debounced, sort, showAll, first?.id]);

  const tray = useAsync<EligibleResponse>(
    () =>
      api.get<EligibleResponse>('/breeding/eligible', {
        sort,
        page,
        search: debounced || undefined,
        partnerId: first?.id,
        compatibleOnly: first ? String(!showAll) : undefined,
      }),
    [sort, page, debounced, first?.id, showAll],
  );

  const nursery = state.data?.nursery;
  const rules = state.data?.rules;
  const pairs = nursery?.pairs ?? [];
  const slots = nursery?.slots ?? 1;
  const free = slots - pairs.length;

  // Ask the server whether the pairing works as soon as two are chosen; it
  // explains *why* not, and names the family — far better than a dead button.
  useEffect(() => {
    if (picked.length !== 2) return setCompat(null);
    let cancelled = false;
    api
      .post<CompatibilityDetail>('/breeding/check', { parentAId: picked[0]!.id, parentBId: picked[1]!.id })
      .then((result) => !cancelled && setCompat(result))
      .catch(() => !cancelled && setCompat(null));
    return () => {
      cancelled = true;
    };
  }, [picked]);

  function toggle(pokemon: BreedablePokemon) {
    setPicked((current) =>
      current.some((entry) => entry.id === pokemon.id)
        ? current.filter((entry) => entry.id !== pokemon.id)
        : current.length >= 2
          ? [current[1]!, pokemon]
          : [...current, pokemon],
    );
  }

  async function start() {
    setBusy('start');
    try {
      await api.post('/breeding/start', { parentAId: picked[0]!.id, parentBId: picked[1]!.id });
      push('success', 'They settled into the Nursery.', '◓');
      setPicked([]);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not start breeding.');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Claims queued eggs. A pair now produces continuously into a queue, so this
   * is a status flip on eggs that already exist rather than the old one-shot
   * "collect" that minted one — which is what makes double-clicking harmless.
   */
  async function claim(pairId?: string) {
    setBusy(pairId ?? 'claim-all');
    try {
      const result = await api.post<{ claimed: number; families: string[] }>('/breeding/claim', {
        ...(pairId ? { pairId } : {}),
        all: true,
      });
      if (result.claimed === 0) {
        push('info', 'Nothing to claim yet.');
      } else {
        push('success', `Claimed ${result.claimed} egg${result.claimed === 1 ? '' : 's'}.`, '◓');
      }
      state.reload();
      refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not claim.');
    } finally {
      setBusy(null);
    }
  }


  async function cancel(pair: NurseryPair) {
    setBusy(pair.id);
    try {
      await api.post('/breeding/cancel', { pairId: pair.id });
      push('info', 'You took them back out. No egg this time.');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const eligibleTotal = state.data?.eligibleTotal ?? 0;
  /**
   * The page the server sent back.
   *
   * The Pokemon already chosen is prepended when it is not on this page, so the
   * selected card never vanishes from under the pointer that just clicked it —
   * it is not its own partner, so the server never returns it.
   */
  const trayRows = tray.data?.pokemon ?? [];
  const shown =
    first && !trayRows.some((row) => row.id === first.id) ? [first, ...trayRows] : trayRows;

  const matched = tray.data?.total ?? 0;
  const trayHint = first
    ? tray.loading
      ? 'Checking…'
      : showAll
        ? `${matched} of ${eligibleTotal} shown`
        : `${matched} of ${eligibleTotal} can pair with ${first.displayName}`
    : debounced
      ? `${matched} of ${eligibleTotal} match "${debounced}"`
      : `${eligibleTotal} available`;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Nursery</h1>
          <div className="page-head__sub">
            Which family a pair produces depends on what they are. Two Water types make a different
            egg than a Water and a Rock.
          </div>
        </div>
        <Link className="btn" to="/hatchery">
          Go to Hatchery
        </Link>
      </div>

      <Panel
        title="❀ Nursery slots"
        hint={`${pairs.length} / ${slots} in use${
          nursery && slots < nursery.maxSlots ? ` · ${nursery.maxSlots} max` : ''
        }`}
      >
        {state.loading && !state.data ? (
          <div className="pxwell skeleton" style={{ height: 180 }} />
        ) : (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {Array.from({ length: slots }, (_, index) => {
              const pair = pairs.find((entry) => entry.slotIndex === index);
              if (!pair) return <EmptySlot key={index} />;
              return (
                <PairCard
                  key={pair.id}
                  pair={pair}
                  now={now}
                  busy={busy === pair.id}
                  onClaim={() => void claim(pair.id)}
                  onCancel={() => void cancel(pair)}
                />
              );
            })}
          </div>
        )}
      </Panel>

      {free > 0 && (
        <Panel title="◈ Choose two Pokémon">
          <div className="stack">
            <div className="row row--wrap" style={{ gap: 'var(--gap)', alignItems: 'center' }}>
              <PickedSlot pokemon={picked[0]} onClear={() => setPicked((c) => c.filter((_, i) => i !== 0))} />
              <span className="mono" style={{ fontSize: '1.4rem' }} aria-hidden>
                +
              </span>
              <PickedSlot pokemon={picked[1]} onClear={() => setPicked((c) => c.filter((_, i) => i !== 1))} />
            </div>

            {picked.length === 2 && compat && (
              <div
                className="pxwell"
                style={{
                  padding: 'var(--gap-sm)',
                  borderTop: `3px solid ${compat.ok ? 'var(--leaf)' : 'var(--berry)'}`,
                }}
              >
                {compat.ok && compat.family ? (
                  <div className="stack-sm">
                    <div className="tiny">
                      These two would produce a <strong>{compat.family.name}</strong>.
                    </div>
                    {/* The band and the sentence — design PART 3. The numeric
                        score is deliberately only a tooltip: casual players get
                        a feeling, optimisers get the number. */}
                    <div className="row row--wrap" style={{ gap: 4 }}>
                      <span className="chip" title={`Compatibility ${compat.score}/100`}>
                        {compat.band.icon} {compat.band.label}
                      </span>
                      {compat.family.ruleName && <span className="chip">{compat.family.ruleName}</span>}
                      {compat.secondsPerEgg && (
                        <span className="chip">An egg about every {formatDuration(compat.secondsPerEgg)}</span>
                      )}
                      {compat.differentTrainers && (
                        <span className="chip" style={{ background: 'var(--gold)', color: '#3a2a06' }}>
                          ✦ different trainers
                        </span>
                      )}
                    </div>
                    <div className="tiny">{compat.band.blurb}</div>
                    <div className="tiny muted">
                      What hatches out of it is still up to the family — check the{' '}
                      <Link to={`/journal/${compat.family.key}`}>Journal</Link>.
                    </div>
                  </div>
                ) : (
                  <div className="tiny" style={{ color: 'var(--berry-dark)' }}>
                    {compat.reason}
                  </div>
                )}
              </div>
            )}

            <Button
              variant="go"
              size="lg"
              block
              disabled={!compat?.ok || busy === 'start'}
              loading={busy === 'start'}
              onClick={() => void start()}
            >
              {picked.length < 2
                ? 'Pick two Pokémon'
                : compat?.ok
                  ? 'Leave them at the Nursery'
                  : 'Not compatible'}
            </Button>
          </div>
        </Panel>
      )}

      <Panel
        title={first ? `Partners for ${first.displayName}` : 'Your Pokémon'}
        hint={trayHint}
        action={
          // Only offered once a first pick is narrowing the list. A player with
          // nothing selected is already seeing everything.
          first ? (
            <Button size="sm" variant="ghost" onClick={() => setShowAll((on) => !on)}>
              {showAll ? 'Only compatible' : `Show all ${eligibleTotal}`}
            </Button>
          ) : undefined
        }
      >
        {/* Search and sort are query parameters, so they run over the whole
            collection rather than over the page on screen. */}
        <div className="row row--wrap" style={{ marginBottom: 'var(--gap-sm)' }}>
          <input
            className="input"
            style={{ flex: '1 1 180px', minWidth: 0 }}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name or nickname"
            aria-label="Search your Pokémon"
          />
          <select
            className="select"
            style={{ flex: '0 1 200px' }}
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            aria-label="Sort"
          >
            <option value="iv">Best genes</option>
            <option value="level">Level high → low</option>
            <option value="levelAsc">Level low → high</option>
            <option value="recent">Recently acquired</option>
            <option value="oldest">Oldest first</option>
            <option value="breeding">Breeding level</option>
            <option value="offspring">Most offspring</option>
            <option value="size">Largest first</option>
            <option value="shiny">Shinies first</option>
            <option value="name">Name A–Z</option>
            <option value="dex">Pokédex order</option>
          </select>
        </div>

        {tray.error ? (
          <ErrorState message={tray.error} onRetry={tray.reload} />
        ) : tray.loading && !tray.data ? (
          <div className="pxwell skeleton" style={{ height: 180 }} />
        ) : eligibleTotal === 0 ? (
          <EmptyState art="◌" title="Nothing available">
            Locked Pokémon can't be bred, anyone already in a slot is busy, and anything listed on
            the Bazaar is spoken for.
          </EmptyState>
        ) : shown.length === 0 ? (
          // Naming the commonest refusal beats an empty grid, which reads as a
          // broken page rather than as an answer. The sentence is the server's.
          <EmptyState
            art="◌"
            title={
              debounced
                ? 'No matches'
                : first
                  ? `Nothing can pair with ${first.displayName}`
                  : 'Nothing available'
            }
          >
            {debounced
              ? 'Try a different search.'
              : (tray.data?.commonestRefusal ??
                'Try a different first pick — or a Ditto, which pairs with almost anything.')}
          </EmptyState>
        ) : (
          <>
            <div className="grove-tray grove-tray--candidates" style={{ maxHeight: 460 }}>
              {shown.map((pokemon) => {
                const chosen = picked.some((entry) => entry.id === pokemon.id);
                const check = pokemon.compatible;
                // Incompatible only ever appears while "Show all" is on, and
                // then it is shown *with its reason* rather than merely greyed
                // — the whole point of `checkCompatibility` returning a
                // sentence.
                const refused = Boolean(first) && pokemon.id !== first?.id && check != null && !check.ok;
                return (
                  <CandidateCard
                    key={pokemon.id}
                    card={pokemon}
                    chosen={chosen}
                    refused={refused}
                    disabled={pokemon.onCooldown || (free === 0 && !chosen) || refused}
                    onPick={toggle}
                  />
                );
              })}
            </div>

            {(tray.data?.totalPages ?? 1) > 1 && (
              <div className="row" style={{ justifyContent: 'center', marginTop: 'var(--gap-sm)' }}>
                <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  ← Prev
                </Button>
                <span className="mono tiny">
                  Page {tray.data?.page} of {tray.data?.totalPages}
                </span>
                <Button
                  size="sm"
                  disabled={page >= (tray.data?.totalPages ?? 1)}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next →
                </Button>
              </div>
            )}
          </>
        )}
      </Panel>

      {rules && (
        <Panel title="How the Nursery works" raised={false}>
          <ul className="tiny muted" style={{ margin: 0, paddingLeft: '1.1em', lineHeight: 1.8 }}>
            <li>
              Both Pokémon must share an egg group and be of opposite genders. Ditto pairs with
              almost anything.
            </li>
            <li>
              The pair's <strong>Egg Family</strong> is decided by what the parents are — types,
              egg groups, whether they're the same species. The egg is a normal family egg, so what
              hatches follows that family's odds.
            </li>
            <li>
              {rules.inheritedIvs} of the six IVs are inherited from the parents; the rest are
              rolled fresh.
              {rules.bonusIvChance > 0 && (
                <> Around {Math.round(rules.bonusIvChance / 10)}% of eggs inherit a fourth.</>
              )}
            </li>
            <li>
              Parents with <strong>different original trainers</strong> improve the shiny odds, so a
              traded Pokémon is worth breeding with.
            </li>
            <li>
              A pair keeps working on its own. How fast depends on how well they get on — between
              about {formatDuration(rules.fastestSeconds)} and {formatDuration(rules.slowestSeconds)}{' '}
              per egg.
            </li>
            <li>
              They stop once <strong>{rules.queueCap} eggs</strong> are waiting, so nothing is ever
              lost by not checking in. Claim some and they start again.
            </li>
          </ul>
        </Panel>
      )}
    </div>
  );
}

/** One shared second-hand rather than a timer per card. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function PairCard({
  pair,
  now,
  busy,
  onClaim,
  onCancel,
}: {
  pair: NurseryPair;
  now: number;
  busy: boolean;
  onClaim: () => void;
  onCancel: () => void;
}) {
  const waiting = pair.queue.length;
  const remaining =
    pair.nextEggAt != null
      ? Math.max(0, Math.floor((new Date(pair.nextEggAt).getTime() - now) / 1000))
      : null;

  return (
    <div className={`pxframe pxframe--raised shelf-card ${waiting > 0 ? 'egg-slot--ready' : ''}`}>
      <div className="row row--between">
        <span className="mono tiny">Slot {pair.slotIndex + 1}</span>
        <span className="chip" title={`Compatibility ${pair.compatibility}/100`}>
          {pair.record ? pair.record.bondName : 'Newly paired'}
        </span>
      </div>

      <div className="row" style={{ justifyContent: 'center', gap: 'var(--gap-sm)', alignItems: 'center' }}>
        <ParentMini pokemon={pair.parentA} />
        <EggArt
          appearance={pair.family.appearance}
          size="sm"
          state={waiting > 0 ? 'ready' : 'idle'}
          label={pair.family.name}
        />
        <ParentMini pokemon={pair.parentB} />
      </div>

      <div className="center stack-sm" style={{ gap: 4 }}>
        <div className="mono tiny">{pair.family.name}</div>
        <div className="tiny muted">{pair.ruleName}</div>
      </div>

      {/* The queue — design PART 27. A pair keeps working until this is full. */}
      <div className="stack-sm">
        <div className="row row--between tiny">
          <span className="muted">Eggs waiting</span>
          <span className="mono">{waiting}</span>
        </div>
        {pair.isPaused ? (
          <div className="tiny muted">
            The queue is full, so they are resting. Claim some to start them again.
          </div>
        ) : remaining != null ? (
          <div className="center tiny muted">Next egg in {formatDuration(remaining)}</div>
        ) : null}
      </div>

      <Button variant="gold" block loading={busy} disabled={waiting === 0} onClick={onClaim}>
        {waiting > 0 ? `Claim ${waiting} egg${waiting === 1 ? '' : 's'}` : 'No eggs yet'}
      </Button>

      <div className="row row--between tiny muted">
        <span>{pair.eggsProduced.toLocaleString()} produced here</span>
        {pair.record && <span>{pair.record.shinyOffspring} shiny</span>}
      </div>

      <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
        Take them back out {waiting > 0 ? '(you keep the eggs)' : ''}
      </Button>
    </div>
  );
}

function EmptySlot() {
  return (
    <div className="pxwell egg-slot egg-slot--empty">
      <div className="egg-slot__plus" aria-hidden>
        +
      </div>
      <div className="tiny mono">Free slot</div>
      <div className="tiny muted center">Pick two Pokémon below.</div>
    </div>
  );
}

function ParentMini({
  pokemon,
}: {
  pokemon: { sprite: string; displayName: string; level: number; isShiny: boolean };
}) {
  return (
    <div className="center" title={`${pokemon.displayName} · Lv ${pokemon.level}`}>
      <img
        className="pixel"
        src={pokemon.sprite}
        alt={pokemon.displayName}
        style={{ width: 56, height: 56 }}
      />
      <div className="tiny muted mono">Lv {pokemon.level}</div>
    </div>
  );
}

function PickedSlot({
  pokemon,
  onClear,
}: {
  pokemon: BreedablePokemon | undefined;
  onClear: () => void;
}) {
  if (!pokemon) {
    return (
      <div className="pxwell" style={{ width: 118, height: 130, display: 'grid', placeItems: 'center' }}>
        <span className="tiny muted">Empty</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`pxframe tray-item ${pokemon.isShiny ? 'pkmn-card--shiny' : ''}`}
      style={{ width: 118 }}
      onClick={onClear}
      title="Remove"
    >
      <img className="pixel" src={pokemon.sprite} alt="" style={{ width: 64, height: 64 }} />
      <span className="tray-item__name">{pokemon.displayName}</span>
      {/* The same three facts the tray card leads with. A slot that dropped
          back to a name and a level made the picker's information vanish at
          exactly the moment the player wants to compare their two choices. */}
      <span className="cand__line">
        {genderSymbol(pokemon.gender) || '—'} · <span className="muted">Lv{pokemon.level}</span> ·{' '}
        <span className="cand__iv">{pokemon.potential.percent}%</span>
      </span>
    </button>
  );
}
